import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Authorization from 'components/global/Authorization';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import Tabs from 'components/global/Tabs';
import { FilterAction } from 'components/list/DynamicListFilterUtils';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocalStorage from 'hooks/useLocalStorage';
import useModalRouter from 'hooks/useModalRouter';

import { ADVANCE_WMS_MODULE, SOUND } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS, { mergePaths } from 'utils/paths';

import { faWaveSquare } from '@fortawesome/pro-regular-svg-icons';
import { faBoxesPacking, faForward, faScannerGun } from '@fortawesome/pro-solid-svg-icons';

import * as S from './Operations.styled';

const PackingInput = () => {
	const inputRef = useRef(null);
	const [value, setValue] = useState(null);
	const { showModal } = useModalRouter();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { search } = useLocation();
	const [autoFocusMode, setAutoFocusMode] = useLocalStorage('autoFocusModePackingList', true);
	const [isFocused, setIsFocused] = useState(false);
	const focusInterval = useRef(null);
	const axiosPrivate = useAxiosPrivate();
	const [loading, setLoading] = useState(false);
	const { showError } = useAlert();

	const handleButtonClick = useCallback(async (value) => {
		setLoading(true);
		if (value) {
			const response = await axiosPrivate
				.post(ADVANCE_WMS_MODULE.GET_PACKING_LIST, {
					paging: {
						offset: 0,
						count: 1
					},
					filter: [
						{
							key: ['orderID', 'id'],
							ignoreCase: false,
							action: FilterAction.is,
							value: [value]
						}
					],
					sort: {
						key: ['id'],
						order: 'desc'
					},
					columns: ['id', 'orderID'],
					totalRecord: false
				})
				.then((res) => {
					if (res?.status === 200) {
						return res.data.data;
					} else {
						AppLogger.error(res);
						showError();
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					showError();
				});

			if (Array.isArray(response) && response.length > 0) {
				const successSound = new Audio(SOUND.BARCODE_SUCCESS);
				successSound.play();
				showModal('packing-form', { id: response[0].id });
				setValue(null);
			} else {
				const errorSound = new Audio(SOUND.BARCODE_ERROR);
				errorSound.play();
				setValue(null);
			}
		}
		setLoading(false);
	}, []);

	const handleKeyDown = useCallback((event, value) => {
		if (event.key === 'Enter') {
			handleButtonClick(value);
		}
	}, []);

	const handleOnFocus = useCallback(() => {
		setIsFocused(true);
	}, []);

	const handleOnBlur = useCallback(() => {
		setIsFocused(false);
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(search);
		const modalName = params.get('modal');

		if (modalName) {
			setIsModalOpen(true);
		} else {
			if (isModalOpen) {
				inputRef.current?.focus();
			}
			setIsModalOpen(false);
		}
	}, [search]);

	useEffect(() => {
		if (!isModalOpen && autoFocusMode && !isFocused) {
			focusInterval.current = setTimeout(() => {
				inputRef.current?.focus();
			}, 200);
		} else {
			clearInterval(focusInterval.current);
		}

		return () => {
			clearInterval(focusInterval.current);
		};
	}, [isFocused, autoFocusMode, isModalOpen]);

	useEffect(() => {
		if (autoFocusMode) inputRef?.current.addEventListener('focusout', handleOnBlur);
		return () => {
			inputRef?.current?.removeEventListener('focusout', handleOnBlur);
		};
	}, [autoFocusMode]);

	return (
		<S.ButtonContainer>
			<Button
				icon={faScannerGun}
				onClick={() => {
					setAutoFocusMode(!autoFocusMode);
				}}
				className={autoFocusMode ? 'green-button' : ''}
			/>

			<S.PackingInputContainer>
				<StringInput
					ref={inputRef}
					value={value}
					setValue={setValue}
					autoFocus
					onFocus={handleOnFocus}
					onBlur={handleOnBlur}
					onKeyDown={(e) => handleKeyDown(e, value)}
				/>
				<Button label="pack" onClick={() => handleButtonClick(value)} loading={loading} />
			</S.PackingInputContainer>
		</S.ButtonContainer>
	);
};
const HeaderButtons = () => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { showModal } = useModalRouter();

	return (
		<>
			{pathname === mergePaths(PATHS.OPERATIONS, PATHS.INBOUND_ORDERS) && (
				<Authorization key="addInventoryOrder" path={`/modal/inventory-order-form`}>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						className="addButton"
						label="addInventoryOrder"
						onClick={() => {
							showModal('inventory-order-form', { mode: 'add' });
						}}
						variant="blue"
					/>
				</Authorization>
			)}
			{pathname === mergePaths(PATHS.OPERATIONS, PATHS.OUTBOUND_ORDERS) && (
				<Authorization key="addInventoryOrder" path={`/modal/inventory-order-form`}>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						className="addButton"
						label="addInventoryOrder"
						onClick={() => {
							showModal('inventory-order-form', { mode: 'add' });
						}}
						variant="blue"
					/>
				</Authorization>
			)}
			{pathname === mergePaths(PATHS.OPERATIONS, PATHS.PUTAWAY) && (
				<>
					<Authorization key="easyPut" path={`/modal/easyput-form`}>
						<Button
							icon={faForward}
							label="easyPut"
							onClick={() => {
								showModal('easyput-form');
							}}
							variant="blue"
						/>
					</Authorization>
					<Authorization key="addPutAway" path={`/modal/putaway-form`}>
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
							className="addButton"
							label="addPutAway"
							onClick={() => {
								showModal('putaway-form', { mode: 'add' });
							}}
							variant="blue"
						/>
					</Authorization>
				</>
			)}
			{pathname === mergePaths(PATHS.OPERATIONS, PATHS.PACKING) && (
				<>
					<Authorization key="packing" path={`/modal/packing-form`}>
						<PackingInput />
					</Authorization>
					<Authorization key="packingPilot" path={`/modal/packing-pilot-form`}>
						<Button
							icon={faBoxesPacking}
							label="packingPilot"
							onClick={() => {
								showModal('packing-pilot-form', { mode: 'add' });
							}}
							variant="blue"
						/>
					</Authorization>
				</>
			)}
			{pathname === mergePaths(PATHS.OPERATIONS, PATHS.WAVES) && (
				<>
					<Authorization key="waveCreationPilot" path={`/modal/wave-pilot-form`}>
						<Button
							icon={faWaveSquare}
							label={t('waveCreationPilot.button')}
							onClick={() => {
								showModal('wave-pilot-form', { mode: 'add' });
							}}
							variant="blue"
						/>
					</Authorization>
				</>
			)}
		</>
	);
};

const Operations = () => {
	const tabs = useMemo(
		() => [
			{
				label: 'inbound',
				path: mergePaths(PATHS.OPERATIONS, PATHS.INBOUND_ORDERS)
			},
			{
				label: 'outbound',
				path: mergePaths(PATHS.OPERATIONS, PATHS.OUTBOUND_ORDERS)
			},
			{
				label: 'putAway',
				path: mergePaths(PATHS.OPERATIONS, PATHS.PUTAWAY)
			},
			{
				label: 'waves',
				path: mergePaths(PATHS.OPERATIONS, PATHS.WAVES)
			},
			{
				label: 'sorting',
				path: mergePaths(PATHS.OPERATIONS, PATHS.SORTING)
			},
			{
				label: 'packingListing',
				path: mergePaths(PATHS.OPERATIONS, PATHS.PACKING)
			},
			{
				label: 'return',
				path: mergePaths(PATHS.OPERATIONS, PATHS.RETURN)
			}
		],
		[]
	);

	return (
		<div>
			<PageHeader
				title="operations"
				lordIcon="https://storage.googleapis.com/tryoto-public/lottie-files-compressed/operations-icon-new.json.gz">
				<HeaderButtons />
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default Operations;
